import React, { useState } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import MainContent, { TableContent } from 'components-shared';
import EmptyScreen from 'packages/ui/EmptyScreen';
import TextFilter from 'components/TextFilter';

import { useSites, useHasSitesOnProduction } from 'store';

import Table from './Table/Table';
import useConnectedUsers from './use-connected-users';

const ConnectedUsers: React.FC = () => {
  const { t } = useTranslation();
  const hasSitesOnProduction = useHasSitesOnProduction();
  const [{ selected }] = useSites();
  const [verbatim, setVerbatim] = useState('');
  const [debouncedVerbatim, setDebouncedVerbatim] = useState('');
  const [infoDismissed, setInfoDismissed] = useState(false);

  const debouncedSetVerbatim = useDebouncedCallback((value: string) => {
    setDebouncedVerbatim(value);
  }, 400);

  const { realtimeCount, loading } = useConnectedUsers();

  return (
    <Stack flex={1}>
      {selected.length !== 1 || !hasSitesOnProduction
        ? (
          <EmptyScreen icon="users" iconSize="80px">
            {!hasSitesOnProduction
              ? t('ui_elements.service_soon')
              : t('supervision.connected_users.no_site_selected')}
          </EmptyScreen>
        ) : (
          <>
            <Stack gap={3} mt={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <TextFilter
                  value={verbatim}
                  placeholder={t('supervision.connected_users.filters.search_placeholder')}
                  width="50vh"
                  onChange={(value: string) => {
                    setVerbatim(value);
                    debouncedSetVerbatim(value);
                  }}
                />
                <Box
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: '#F7F7F7',
                    height: '32px',
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#464646' }}>
                    {t('supervision.connected_users.realtime_count', { count: realtimeCount })}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
            {!infoDismissed && (loading || realtimeCount > 0) && (
              <Box
                sx={{
                  position: 'relative',
                  backgroundColor: '#F0F6FF',
                  border: '1px solid #C8DFFE',
                  borderRadius: '10px',
                  pr: 4,
                  pl: 2,
                  py: 1,
                  mt: 1
                }}
              >
                <Typography sx={{ fontSize: 12, color: '#464646', lineHeight: 1.5, textAlign: 'left' }}>
                  {t('supervision.connected_users.info_toast')}
                </Typography>
                <Box
                  onClick={() => setInfoDismissed(true)}
                  sx={{ position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)', cursor: 'pointer' }}
                >
                  <i className="fa-light fa-xmark" style={{ fontSize: 12, color: '#888' }} />
                </Box>
              </Box>
            )}
            <MainContent>
              <TableContent>
                <Table verbatim={debouncedVerbatim} />
              </TableContent>
              <Outlet />
            </MainContent>
          </>
        )}
    </Stack>
  );
};

export default ConnectedUsers;